import { useState } from 'react';

import BlueprintDemoModalContents from 'dashboard/components/BlueprintDemoModalContents';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import {
  BlueprintPreviewMap,
  BlueprintServerMetadata,
  IndustryName,
  UseCaseV2Name,
} from 'dashboard/models/blueprint';
import { BuilderPreviewMap } from 'dashboard/models/inquiry-template';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { BlueprintsQuizV2Industry } from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2Industry';
import { BlueprintsQuizV2Results } from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2Results';
import { BlueprintsQuizV2Start } from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2Start';
import { BlueprintsQuizV2UseCases } from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2UseCases';
import { SegmentClient } from 'lib/clients/SegmentClient';

interface BlueprintsQuizV2ModalProps {
  currentOrganizationPlan: OrganizationPlan | null;
  pendingOrganizationPlanMigration: OrganizationPlanMigration;
  selectedIndustry: IndustryName;
  selectedUseCase: UseCaseV2Name;
  quizResultBlueprintKey: string;
  blueprintsCatalogServer: BlueprintServerMetadata[];
  blueprintPreviews: BlueprintPreviewMap;
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  previouslyUsedBlueprintKeys: string[];
  handleIndustrySubmit: (selectedOption: IndustryName) => void;
  handleUseCaseSubmit: (selectedOption: UseCaseV2Name) => void;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
  createBlueprint: (blueprintKey: string) => Promise<void>;
  closeModal: () => void; // pass in null if modal should be non-dismissable
  closeModalOnCompletion: () => void;
  navigateToBlueprintsAllModal: () => void;
}

enum QuizScreen {
  Start = 'start',
  Industry = 'industry',
  UseCases = 'use-cases',
  Results = 'results',
  BlueprintPreview = 'blueprint-preview',
}

const BlueprintsQuizV2Modal = ({
  currentOrganizationPlan,
  pendingOrganizationPlanMigration,
  selectedIndustry,
  selectedUseCase,
  quizResultBlueprintKey,
  blueprintsCatalogServer,
  blueprintPreviews,
  inquiryTemplateBuilderPreviews,
  previouslyUsedBlueprintKeys,
  handleIndustrySubmit,
  handleUseCaseSubmit,
  handleLoadBlueprintPreview,
  handleLoadInquiryTemplateBuilderPreview,
  createBlueprint,
  closeModal,
  closeModalOnCompletion,
  navigateToBlueprintsAllModal,
}: BlueprintsQuizV2ModalProps) => {
  const [currentScreen, setCurrentScreen] = useState<QuizScreen>(QuizScreen.Start);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [blueprintKeyForPreview, setBlueprintKeyForPreview] = useState<string>(null);

  return (
    <ModalContainer size={currentScreen === QuizScreen.BlueprintPreview ? 'xl' : 'lg'}>
      {currentScreen === 'start' && (
        <BlueprintsQuizV2Start
          currentOrganizationPlan={currentOrganizationPlan}
          pendingOrganizationPlanMigration={pendingOrganizationPlanMigration}
          toNextScreen={() => {
            setCurrentScreen(QuizScreen.Industry);
            SegmentClient.sendSegmentTrackEvent('Blueprints Quiz V2 - Started');
          }}
          closeModal={closeModal}
        />
      )}
      {currentScreen === QuizScreen.Industry && (
        <BlueprintsQuizV2Industry
          selectedIndustry={selectedIndustry}
          toPreviousScreen={() => setCurrentScreen(QuizScreen.Start)}
          toNextScreen={() => setCurrentScreen(QuizScreen.UseCases)}
          submit={handleIndustrySubmit}
          closeModal={closeModal}
        />
      )}
      {currentScreen === QuizScreen.UseCases && (
        <BlueprintsQuizV2UseCases
          selectedIndustry={selectedIndustry}
          selectedUseCase={selectedUseCase}
          toPreviousScreen={() => setCurrentScreen(QuizScreen.Industry)}
          toNextScreen={() => setCurrentScreen(QuizScreen.Results)}
          submit={handleUseCaseSubmit}
          closeModal={closeModal}
        />
      )}
      {currentScreen === QuizScreen.Results && (
        <BlueprintsQuizV2Results
          quizResultBlueprintKey={quizResultBlueprintKey}
          blueprintsCatalogServer={blueprintsCatalogServer}
          previouslyUsedBlueprintKeys={previouslyUsedBlueprintKeys}
          toPreviousScreen={() => setCurrentScreen(QuizScreen.UseCases)}
          toBlueprintPreview={(blueprintKey) => {
            setBlueprintKeyForPreview(blueprintKey);
            setCurrentScreen(QuizScreen.BlueprintPreview);
          }}
          createBlueprint={createBlueprint}
          closeModal={closeModal}
          closeModalOnCompletion={closeModalOnCompletion}
          navigateToBlueprintsAllModal={navigateToBlueprintsAllModal}
        />
      )}
      {currentScreen === QuizScreen.BlueprintPreview && (
        <BlueprintDemoModalContents
          blueprintKey={blueprintKeyForPreview}
          blueprintsCatalogServer={blueprintsCatalogServer}
          blueprintPreviews={blueprintPreviews}
          inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
          isBlueprintAddedToSandbox={false}
          handleLoadBlueprintPreview={handleLoadBlueprintPreview}
          handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
          toPreviousScreen={() => setCurrentScreen(QuizScreen.Results)}
          closeModal={closeModal}
        />
      )}
    </ModalContainer>
  );
};

export default BlueprintsQuizV2Modal;
